import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Query Key Factory ---
export const handicapOpsKeys = {
  all: ['handicap-ops'] as const,
  types: () => [...handicapOpsKeys.all, 'types'] as const,
  // Conditional spread keeps invalidateQueries prefix matching working —
  // a trailing `undefined` in the key would defeat partialDeepEqual.
  userAvailable: (userId: string, orgId?: string) =>
    orgId
      ? ([...handicapOpsKeys.all, 'user', userId, 'available', orgId] as const)
      : ([...handicapOpsKeys.all, 'user', userId, 'available'] as const),
  pendingApprovals: (filters?: PendingApprovalsParams) =>
    filters
      ? ([...handicapOpsKeys.all, 'pending', filters] as const)
      : ([...handicapOpsKeys.all, 'pending'] as const),
};

// --- Types ---
interface HandicapType {
  id: string;
  name: string;
  description?: string;
  [key: string]: unknown;
}

interface UserHandicap {
  id: string;
  user_profile_id: string;
  handicap_type_id: string;
  rating: number;
  [key: string]: unknown;
}

interface SelfEntryData {
  handicap_type_id: string;
  rating: number;
  organization_id?: string;
}

interface ApproveHandicapData {
  approved: boolean;
  notes?: string;
}

interface ValidateHandicapsData {
  user_profile_id: string;
  event_id: string;
}

interface BulkUpsertData {
  handicaps: Array<{
    user_profile_id: string;
    handicap_type_id: string;
    rating: number;
    organization_id?: string;
  }>;
}

interface PendingApprovalsParams {
  page?: number;
  pageSize?: number;
}

// --- Query Hooks ---
export function useFilteredHandicapTypes() {
  return useQuery({
    queryKey: handicapOpsKeys.types(),
    queryFn: async () => {
      const res = await httpClient.get<{ data: HandicapType[]; success: boolean }>('/handicap-ops/types/filtered');
      return res.data;
    },
  });
}

export function useUserAvailableHandicaps(userId: string, orgId?: string) {
  const params = orgId ? `?organization_id=${orgId}` : '';
  return useQuery({
    queryKey: handicapOpsKeys.userAvailable(userId, orgId),
    queryFn: async () => {
      const res = await httpClient.get<{ data: UserHandicap[]; success: boolean }>(`/handicap-ops/user/${userId}/available${params}`);
      return res.data;
    },
    enabled: !!userId,
  });
}

export function usePendingApprovals(params?: PendingApprovalsParams) {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: handicapOpsKeys.pendingApprovals(params),
    queryFn: () => httpClient.get<{ data: UserHandicap[]; total: number }>(
      `/handicap-ops/pending-approvals${query}`,
    ),
  });
}

// --- Mutation Hooks ---
export function useSelfEntryHandicap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SelfEntryData) =>
      httpClient.post<UserHandicap>('/handicap-ops/self-entry', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: handicapOpsKeys.all });
    },
  });
}

export function useApproveHandicap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: ApproveHandicapData & { id: string }) =>
      httpClient.put<UserHandicap>(`/handicap-ops/${id}/approve`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: handicapOpsKeys.all });
    },
  });
}

export function useValidateHandicaps() {
  return useMutation({
    mutationFn: (data: ValidateHandicapsData) =>
      httpClient.post<{ valid: boolean; issues?: string[] }>(
        '/handicap-ops/validate',
        data,
      ),
  });
}

export function useBulkUpsertHandicaps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkUpsertData) =>
      httpClient.post<{ upserted: number }>('/handicap-ops/bulk-upsert', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: handicapOpsKeys.all });
    },
  });
}
